import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const CookiePreferences = ({ categories }) => {
  const [preferences, setPreferences] = useState({});
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    // Load saved preferences from localStorage
    const savedPrefs = localStorage.getItem('cookie_preferences');
    if (savedPrefs) {
      setPreferences(JSON.parse(savedPrefs));
    } else {
      // Default: essential only
      const defaultPrefs = {};
      categories?.forEach(cat => {
        defaultPrefs[cat?.id] = cat?.required;
      });
      setPreferences(defaultPrefs);
    }
  }, [categories]);

  const handleToggle = (categoryId, required) => {
    if (required) return; // Cannot toggle required cookies
    
    setPreferences(prev => ({
      ...prev,
      [categoryId]: !prev?.[categoryId]
    }));
    setSaved(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookie_preferences', JSON.stringify(preferences));
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleAcceptAll = () => {
    const allAccepted = {};
    categories?.forEach(cat => {
      allAccepted[cat?.id] = true;
    });
    setPreferences(allAccepted);
    localStorage.setItem('cookie_preferences', JSON.stringify(allAccepted));
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleRejectAll = () => {
    const essentialOnly = {};
    categories?.forEach(cat => {
      essentialOnly[cat?.id] = cat?.required;
    });
    setPreferences(essentialOnly);
    localStorage.setItem('cookie_preferences', JSON.stringify(essentialOnly));
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-foreground mb-2">Cookie Preferences</h2>
          <p className="text-muted-foreground text-sm">
            Manage your cookie consent settings. Changes take effect immediately.
          </p>
        </div>
        {saved && (
          <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
            <Icon name="CheckCircle" size={20} />
            <span className="text-sm font-medium">Saved</span>
          </div>
        )}
      </div>
      <div className="space-y-4 mb-6">
        {categories?.map((category) => (
          <div 
            key={category?.id} 
            className="flex items-start justify-between p-4 bg-muted/50 rounded-lg border border-border hover:border-accent/50 transition-colors"
          >
            <div className="flex-1 pr-4">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-semibold text-foreground">{category?.name}</h3>
                {category?.required && (
                  <span className="px-2 py-0.5 bg-accent/20 text-accent text-xs font-medium rounded">
                    Always Active
                  </span>
                )}
              </div>
              <p className="text-sm text-muted-foreground">{category?.description}</p>
            </div>
            <button
              onClick={() => handleToggle(category?.id, category?.required)}
              disabled={category?.required}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 ${
                preferences?.[category?.id] 
                  ? 'bg-accent' :'bg-gray-300 dark:bg-gray-600'
              } ${category?.required ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              aria-label={`Toggle ${category?.name}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  preferences?.[category?.id] ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-3">
        <button
          onClick={handleSavePreferences}
          className="px-6 py-2 bg-accent text-white rounded-lg hover:bg-accent/90 transition-colors font-medium flex items-center gap-2"
        >
          <Icon name="Save" size={18} />
          Save Preferences
        </button>
        <button
          onClick={handleAcceptAll}
          className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
        >
          Accept All
        </button>
        <button
          onClick={handleRejectAll}
          className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
        >
          Reject Non-Essential
        </button>
      </div>
    </div>
  );
};

export default CookiePreferences;